/**
 * Score a user's picks against official results.
 *
 * Rules:
 * - Exact match at a position awards 3 points.
 * - Driver in top-10 but wrong position awards 1 point.
 * - Duplicates are resolved by taking the highest-scoring occurrence for a driver,
 *   so the same driver can only count once toward bonus totals.
 * - Bonus: if correctPositions > 4, add (correctPositions - 2) bonus points.
 * - DNF: user picked a driver who actually DNF'd → 2 points.
 * - No DNF: user picked null (No DNF) and there were no DNFs → 1 point.
 * - Bonus Questions: if user's answer matches correct_answer → award question's points.
 *
 * Parameters:
 * - officialResults: Array of driver ids in official finishing order (index 0 = position 1)
 * - userPicks: Array of pick objects/ids, index === position-1
 * - officialDnfs: Array of driver ids who DNF'd (omit or pass [] if unknown/none)
 * - userDnfPick: The driver id the user picked to DNF, or null for "No DNF",
 *               or undefined if DNF data is unavailable (no points awarded)
 * - questions: Array of question objects with { id, correct_answer, points } (optional)
 * - bonusAnswersStr: Delimited string "qId:answer,qId:answer" or null (optional)
 */
function normalizeDriverId(value) {
    if (value == null || value === '') return null;
    const numeric = Number(value?.id ?? value);
    return Number.isFinite(numeric) ? numeric : null;
}

function getPositionAward(officialResults = [], positionIndex, pickValue) {
    const pickId = normalizeDriverId(pickValue);
    if (pickId == null) return 0;

    const officialId = normalizeDriverId(officialResults[positionIndex]);
    if (officialId != null && pickId === officialId) return 3;

    const scorableOfficialResults = (officialResults || []).slice(3, 10)
        .map((driverId) => normalizeDriverId(driverId))
        .filter((driverId) => driverId != null);

    return scorableOfficialResults.includes(pickId) ? 1 : 0;
}

export function scorePicksBreakdown(officialResults = [], userPicks = [], officialDnfs = [], userDnfPick = undefined, questions = [], bonusAnswersStr = null) {
    let points = 0;
    const maxPos = Math.max(officialResults.length, Array.isArray(userPicks) ? userPicks.length : 0);

    const positionPoints = {};
    const bestAwardByDriver = new Map();
    const bestPositionByDriver = new Map();

    for (let pos = 4; pos <= maxPos; pos++) {
        const pick = userPicks[pos - 1] ?? null;
        const pickId = pick?.id ?? pick;
        const normalizedPickId = normalizeDriverId(pickId);
        if (normalizedPickId == null) {
            positionPoints[pos] = 0;
            continue;
        }

        const rawAward = getPositionAward(officialResults, pos - 1, pickId);
        const currentBest = bestAwardByDriver.get(normalizedPickId);
        if (currentBest == null || rawAward > currentBest) {
            bestAwardByDriver.set(normalizedPickId, rawAward);
            bestPositionByDriver.set(normalizedPickId, pos);
        }
    }

    let correctPositions = 0;
    for (let pos = 4; pos <= maxPos; pos++) {
        const pick = userPicks[pos - 1] ?? null;
        const pickId = pick?.id ?? pick;
        const normalizedPickId = normalizeDriverId(pickId);
        const bestPos = normalizedPickId == null ? null : bestPositionByDriver.get(normalizedPickId);
        const isBestDuplicatePosition = bestPos === pos;
        let awarded = 0;

        if (isBestDuplicatePosition) {
            awarded = bestAwardByDriver.get(normalizedPickId) || 0;
            if (awarded > 0) {
                correctPositions++;
            }
        }

        points += awarded;
        positionPoints[pos] = awarded;
    }

    let bonusPoints = 0;
    if (correctPositions >= 4) {
        bonusPoints = 1 + (correctPositions - 4) * 2;
        points += bonusPoints;
    }

    // DNF scoring — only when DNF data is provided (userDnfPick !== undefined)
    let dnfPoints = 0;
    if (userDnfPick !== undefined) {
        const dnfIds = (officialDnfs || []).map((d) => Number(d?.id ?? d));
        if (userDnfPick === null) {
            if (dnfIds.length === 0) {
                dnfPoints = 1;
                points += 1;
            }
        } else {
            const pickDnfId = Number(userDnfPick);
            if (dnfIds.includes(pickDnfId)) {
                dnfPoints = 2;
                points += 2;
            }
        }
    }

    // Bonus question scoring
    const questionPoints = {};
    const userAnswers = {};

    if (bonusAnswersStr) {
        bonusAnswersStr.split(',').forEach((pair) => {
            const [qId, ans] = String(pair || '').split(':');
            const normalizedQId = String(qId || '').trim();
            const normalizedAnswer = String(ans || '').trim();
            const parsedAnswer = Number(normalizedAnswer);

            if (normalizedQId && normalizedAnswer && Number.isInteger(parsedAnswer)) {
                userAnswers[normalizedQId] = parsedAnswer;
            }
        });
    }

    if (questions && questions.length > 0 && bonusAnswersStr) {
        questions.forEach((q) => {
            const correctAnswer = q.correct_answer != null ? Number(q.correct_answer) : null;
            const userAnswer = userAnswers[String(q.id)];
            if (Number.isInteger(correctAnswer) && Number.isInteger(userAnswer) && userAnswer === correctAnswer) {
                const awarded = Number(q.points);
                const safeAwarded = Number.isFinite(awarded) ? awarded : 1;
                questionPoints[String(q.id)] = safeAwarded;
                points += safeAwarded;
            } else {
                questionPoints[String(q.id)] = 0;
            }
        });
    } else if (questions && questions.length > 0) {
        questions.forEach((q) => {
            questionPoints[String(q.id)] = 0;
        });
    }

    return {
        points,
        bonusPoints,
        dnfPoints,
        positionPoints,
        questionPoints,
    };
}

export function scorePicks(officialResults = [], userPicks = [], officialDnfs = [], userDnfPick = undefined, questions = [], bonusAnswersStr = null) {
    const breakdown = scorePicksBreakdown(officialResults, userPicks, officialDnfs, userDnfPick, questions, bonusAnswersStr);
    return breakdown.points;
}

export default scorePicks;

